import * as _ from 'lodash';
import { IntlShape } from 'react-intl';
import translationMessages from 'app/translationMessages';

const SPECIES = ['cat', 'dog', 'rabbit', 'ferret', 'rodent', 'reptile', 'bird', 'other'] as const;
export type RestrictedSpeciesType = typeof SPECIES[number];
// string is used to allow custom species, which are not in the list of default species
// We use the string & {} to keep to literal types available for intellisense
export type SpeciesType = RestrictedSpeciesType | (string & {});

const DEFAULT_SPECIE = 'dog';

const SPECIES_ALIASES: Partial<Record<SpeciesType, string[]>> = {
  cat: ['chat', 'ct', 'feline', 'cht'],
  dog: ['cn', 'chien', 'canine', 'golden', 'epagneul', 'chizn', 'canin', 'chie'],
  rabbit: ['lapin', 'nac', 'lp'],
  ferret: ['furet'],
  rodent: ['chinchilla', 'cobaye', 'rat'],
  reptile: [],
  bird: [],
};

const makeSpeciesOptions = (intl: IntlShape) => {
  const species = SPECIES.map((specie) => ({
    label: intl.formatMessage({ id: `patient_info.specie.${specie}` }),
    value: specie,
  }));
  return _.sortBy(species, ['label']);
};

const RACES: Partial<Record<SpeciesType, string[]>> = {
  dog: [
    'afghan_hound',
    'akita_inu',
    'american_akita',
    'american_bulldog',
    'american_cocker_spaniel',
    'american_staffordshire_terrier',
    'australian_cattle_dog',
    'australian_shepherd',
    'basenji',
    'basset',
    'beagle',
    'beauceron',
    'belgian_shepherd',
    'bernese_mountain_dog',
    'bichon',
    'bobtail',
    'border_collie',
    'borzoi',
    'boston_terrier',
    'boxer',
    'bull_terrier',
    'bullmastiff',
    'cairn_terrier',
    'cane_corso',
    'cavalier_king_charles',
    'chart_polski',
    'chihuahua',
    'chow_chow',
    'cocker_spaniel',
    'collie',
    'cotton_tulear',
    'dachshund',
    'dalmatian',
    'doberman_pinscher',
    'dogo_argentino',
    'dogue_de_bordeaux',
    'english_bulldog',
    'fox_terrier',
    'french_bulldog',
    'german_mastiff',
    'german_shepherd',
    'german_shorthaired_pointer',
    'golden_retriever',
    'greyhound',
    'griffin',
    'hovawart',
    'husky',
    'irish_water_dog',
    'irish_wolfhound',
    'italian_greyhound',
    'jack_russell_terrier',
    'labrador',
    'leonberg',
    'lhasa_apso',
    'magyar_agar',
    'mastiff',
    'newfoundland',
    'pinscher',
    'poodle',
    'portuguese_water_dog',
    'pug',
    'rottweiler',
    'saint_bernard',
    'samoyed',
    'schnauzer',
    'scottish_deerhound',
    'setter',
    'shar-pei',
    'shiba-inu',
    'shih_tzu',
    'sloughi_touareg',
    'spaniel',
    'spanish_greyhound',
    'spitz',
    'springer',
    'staffordshire_bull_terrier',
    'swiss_white_shepherd',
    'weimaraner',
    'welsh_corgi',
    'west_highland_white_terrier',
    'whippet',
    'yorkshire_terrier',
  ],
  cat: [
    'abyssinian',
    'american_bobtail',
    'american_curl',
    'american_shorthair',
    'bengal',
    'bombay',
    'british_longhair',
    'british_shorthair',
    'burmese',
    'chartreux',
    'devon_rex',
    'egyptian_mau',
    'european',
    'exotic_shorthair',
    'maine_coon',
    'munchkin',
    'norwegian',
    'persian',
    'ragdoll',
    'sacred_of_burma',
    'savannah',
    'scottish_fold',
    'siamese',
    'siberian',
    'somali',
    'sphynx',
    'turkish_angora',
  ],
  rabbit: [],
  ferret: [],
  rodent: [],
  reptile: [],
  bird: [],
} as const;
const DEFAULT_CAT_RACE = 'european';

const CAT_VHS_REFERENCES = [
  {
    key: 'cat.other',
    lower: 7.2,
    upper: 7.8,
    bibli: 'Litster et Buchanan, 2000',
  },
];
Object.freeze(CAT_VHS_REFERENCES);

const DOG_VHS_REFERENCES = [
  {
    key: 'dog.other',
    lower: 9.2,
    upper: 10.2,
    bibli: 'Buchanan et Bücheler, 1995',
  },
  {
    key: 'dog.beagle',
    lower: 10.1,
    upper: 10.9,
    bibli: 'Kraetschmer et al., 2008',
  },
  {
    key: 'dog.german_shepherd',
    lower: 8.9,
    upper: 10.5,
    bibli: 'Lamb et al., 2001',
  },
  {
    key: 'dog.boston_terrier',
    lower: 10.3,
    upper: 13.1,
    bibli: 'Jepsen-Grant, Pollard et Johnson, 2012',
  },
  {
    key: 'dog.boxer',
    lower: 10.8,
    upper: 12.4,
    bibli: 'Lamb et al., 2001',
  },
  {
    key: 'dog.english_bulldog',
    lower: 11,
    upper: 14.4,
    bibli: 'Jepsen-Grant, Pollard et Johnson, 2012',
  },
  {
    key: 'dog.pug',
    lower: 9.8,
    upper: 11.6,
    bibli: 'Jepsen-Grant, Pollard et Johnson, 2012',
  },
  {
    key: 'dog.cavalier_king_charles',
    lower: 10.1,
    upper: 11.1,
    bibli: 'Lamb et al., 2001',
  },
  {
    key: 'dog.greyhound',
    lower: 10.4,
    upper: 10.6,
    bibli: 'Marin et al., 2007',
  },
  {
    key: 'dog.labrador',
    lower: 10.3,
    upper: 10.5,
    bibli: 'Jepsen-Grant, Pollard et Johnson, 2012',
  },
  {
    key: 'dog.lhasa_apso',
    lower: 8.8,
    upper: 10.4,
    bibli: 'Jepsen-Grant, Pollard et Johnson, 2012',
  },
  {
    key: 'dog.rottweiler',
    lower: 9.7,
    upper: 9.9,
    bibli: 'Marin et al., 2007',
  },
  {
    key: 'dog.shih_tzu',
    lower: 8.9,
    upper: 10.1,
    bibli: 'Jepsen-Grant, Pollard et Johnson, 2012',
  },
] as const;
Object.freeze(DOG_VHS_REFERENCES);

const makeRacesOptions = (intl: IntlShape, specie: SpeciesType) => {
  if (!specie) {
    return [];
  }
  const races = _.map(RACES[specie], (race) => ({
    label: intl.formatMessage({ id: `patient_info.specie.${specie}.race.${race}` }),
    value: race,
  }));
  return _.sortBy(races, ['label']);
};

export {
  SPECIES,
  DEFAULT_SPECIE,
  SPECIES_ALIASES,
  RACES,
  DEFAULT_CAT_RACE,
  CAT_VHS_REFERENCES,
  DOG_VHS_REFERENCES,
  makeSpeciesOptions,
  makeRacesOptions,
};
